import {Controller} from "@hotwired/stimulus"
// Import lucide if possible to re-render, otherwise we might rely on a global or observer
// Assuming lucide is available globally or we just use the attributes.

export default class extends Controller {
  static targets = ["paginationSelect", "pageFields", "sizeFields", "skipFields", "paramsContainer", "loopsContainer", "testResults", "overlay", "apiCredentialSelect", "restApiConfig", "cloudConfig"]

  connect() {
    this.togglePagination()
    this.toggleSourceType()
  }

  toggleSourceType() {
    if (!this.hasApiCredentialSelectTarget) return

    const select = this.apiCredentialSelectTarget
    const selectedOption = select.options[select.selectedIndex]
    // If no option selected (prompt), default to REST or hide all?
    // Let's default to REST for now as it's the most common/complex
    const sourceType = selectedOption ? selectedOption.getAttribute('data-source-type') : 'rest_api'

    if (sourceType === 'rest_api' || !sourceType) {
      if (this.hasRestApiConfigTarget) {
        this.restApiConfigTarget.classList.remove('hidden')
        this.restApiConfigTarget.querySelectorAll('input, select, textarea').forEach(el => el.disabled = false)
      }
      if (this.hasCloudConfigTarget) {
        this.cloudConfigTarget.classList.add('hidden')
        this.cloudConfigTarget.querySelectorAll('input, select, textarea').forEach(el => el.disabled = true)
      }
    } else if (['s3', 'gcs', 'azure'].includes(sourceType)) {
      if (this.hasRestApiConfigTarget) {
        this.restApiConfigTarget.classList.add('hidden')
        this.restApiConfigTarget.querySelectorAll('input, select, textarea').forEach(el => el.disabled = true)
      }
      if (this.hasCloudConfigTarget) {
        this.cloudConfigTarget.classList.remove('hidden')
        this.cloudConfigTarget.querySelectorAll('input, select, textarea').forEach(el => el.disabled = false)
      }
    }
  }

  showOverlay(event) {
    if (!event.currentTarget.form.checkValidity()) return
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
      if (window.lucide && window.lucide.createIcons) {
        window.lucide.createIcons();
      }
    }
  }

  togglePagination() {
    const type = this.paginationSelectTarget.value

    // Hide all first
    this.pageFieldsTargets.forEach(el => el.classList.add("hidden"))
    this.skipFieldsTargets.forEach(el => el.classList.add("hidden"))

    if (type === "page") {
      this.pageFieldsTargets.forEach(el => el.classList.remove("hidden"))
    } else if (type === "skip_top") {
      this.skipFieldsTargets.forEach(el => el.classList.remove("hidden"))
    } else if (type === "both") {
      this.pageFieldsTargets.forEach(el => el.classList.remove("hidden"))
      this.skipFieldsTargets.forEach(el => el.classList.remove("hidden"))
      // Size parameter is redundant when skip/top controls the limit
      this.sizeFieldsTargets.forEach(el => el.classList.add("hidden"))
    }
  }

  addLoop(event) {
    event.preventDefault()
    const timestamp = new Date().getTime()
    const template = `
      <div class="loop-item relative group mb-4 overflow-hidden rounded-card border border-hair bg-surface-subtle" data-controller="icons">
        <button type="button" data-action="data-source-form#removeLoop" class="absolute top-2 right-2 z-10 flex items-center gap-1 p-1 text-meta font-medium text-ink-faint hover:text-state-bad transition-colors">
          <i data-lucide="trash-2" class="w-4 h-4"></i>
        </button>
        <div class="px-4 py-3 border-b border-hair bg-surface-sunken">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 pr-8">
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Loop Strategy</label>
              <select name="data_source[filter_config][loops][${timestamp}][type]"
                      data-action="change->data-source-form#toggleLoopItem"
                      class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft">
                  <option value="date">Date Based Loop</option>
                  <option value="array">Array Based Loop</option>
              </select>
            </div>
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Parameter Location</label>
              <select name="data_source[filter_config][loops][${timestamp}][param_location]"
                      class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft">
                  <option value="query">Query Param</option>
                  <option value="header">Header</option>
                  <option value="path">URL Path</option>
              </select>
            </div>
            <div class="date-fields">
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Date Style</label>
              <select name="data_source[filter_config][loops][${timestamp}][date_style]"
                      data-action="change->data-source-form#toggleDateStyle"
                      class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft">
                  <option value="single">Single Date ($date)</option>
                  <option value="range">Date Range ($start_date & $end_date)</option>
              </select>
            </div>
          </div>
        </div>

        <div class="loop-fields date-fields p-4 space-y-4">

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Parameter Name</label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][date_param]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="created_at_min">
            </div>
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Parameter Value</label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][date_value]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="$date">
              <div class="flex flex-wrap gap-1 mt-1">
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">$date</span>
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">$start_date</span>
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">$end_date</span>
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">$current_date</span>
              </div>
            </div>
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Batch Size</label>
              <select name="data_source[filter_config][loops][${timestamp}][batch_size]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft">
                <option value="none">None (1 day)</option>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="half_year">Half Year</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Date Format <span class="text-ink-faint font-normal normal-case">(strftime)</span></label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][date_format]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="%Y-%m-%d">
              <div class="flex flex-wrap gap-1 mt-1">
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">%Y%m%d → 20260601</span>
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">%Y/%m/%d → 2026/06/01</span>
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">%d-%m-%Y → 01-06-2026</span>
              </div>
            </div>
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Start Date</label>
              <input type="date" name="data_source[filter_config][loops][${timestamp}][start_date]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft">
            </div>
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">End Date <span class="text-ink-faint font-normal normal-case">(optional)</span></label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][end_date]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="blank = today">
              <div class="flex flex-wrap gap-1 mt-1">
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">2026-03-31</span>
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">$current_date</span>
                <span class="rounded-ctl bg-surface-sunken px-1.5 py-0.5 font-mono text-eyebrow text-ink-muted">$current_date.prev_month.end_of_month</span>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Lookback Window <span class="text-ink-faint font-normal normal-case">(days)</span></label>
              <input type="number" name="data_source[filter_config][loops][${timestamp}][lookback_window_days]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="0" min="0">
              <p class="mt-1 text-meta text-ink-faint">Days to re-sync on each run. 0 = full history.</p>
            </div>
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Combine Separator <span class="text-ink-faint font-normal normal-case">(optional)</span></label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][combine_with]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder=" and ">
              <p class="mt-1 text-meta text-ink-faint">Appends to same param from a prior loop.</p>
            </div>
          </div>

          <div class="end-date-fields grid grid-cols-1 sm:grid-cols-3 gap-3 hidden">
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">End Date Parameter</label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][end_date_param]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="toDate">
            </div>
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">End Date Value</label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][end_date_value]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="$end_date">
            </div>
          </div>

          <div class="rounded-card border border-hair bg-surface-subtle p-3">
            <p class="flex items-center gap-1.5 mb-2 text-eyebrow font-semibold uppercase text-ink-faint">
              <i data-lucide="hard-drive" class="w-3.5 h-3.5 text-ink-faint"></i>
              Save to Table Columns
            </p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Store Start Date As</label>
                <input type="text" name="data_source[filter_config][loops][${timestamp}][storage_col_start]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="sync_start_date">
              </div>
              <div class="end-date-fields hidden">
                <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Store End Date As</label>
                <input type="text" name="data_source[filter_config][loops][${timestamp}][storage_col_end]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="sync_end_date">
              </div>
            </div>
            <p class="mt-1.5 text-meta text-ink-faint">Column names to store loop dates in each data row. Leave blank to skip.</p>
          </div>

          <div class="rounded-card border border-hair bg-surface-subtle p-3">
            <p class="flex items-center gap-1.5 mb-2 text-eyebrow font-semibold uppercase text-ink-faint">
              <i data-lucide="calendar-clock" class="w-3.5 h-3.5 text-ink-faint"></i>
              Changed Dates Reference
            </p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Reference Table</label>
                <select name="data_source[filter_config][loops][${timestamp}][changed_dates_table]"
                        class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft">
                  <option value="">None (disabled)</option>
                  ${this.availableTableOptions()}
                </select>
              </div>
              <div>
                <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Date Column Name</label>
                <input type="text" name="data_source[filter_config][loops][${timestamp}][changed_dates_column]"
                       class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono"
                       placeholder="changed_posting_date">
              </div>
            </div>
            <p class="mt-1.5 text-meta text-ink-faint">On subsequent syncs, dates from this table are merged with the lookback window. Data is read from the previous run. Leave empty to use lookback only.</p>
          </div>
        </div>

        <div class="loop-fields array-fields p-4 space-y-3 hidden">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Parameter Name</label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][array_param]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="store_id">
            </div>
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Combine Separator</label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][combine_with]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder=" and ">
              <p class="mt-1 text-meta text-ink-faint">Appends to same param from a prior loop</p>
            </div>
          </div>
          <div>
            <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Values (Comma Separated)</label>
            <textarea name="data_source[filter_config][loops][${timestamp}][array_values]" rows="2" class="block w-full rounded-ctl border border-hair-hi bg-surface px-2.5 py-1.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="101, 102, 103"></textarea>
          </div>
          <div class="mt-2 rounded-card border border-hair bg-surface-subtle p-3">
            <p class="flex items-center gap-1.5 mb-2 text-eyebrow font-semibold uppercase text-ink-faint">
              <i data-lucide="hard-drive" class="w-3.5 h-3.5 text-ink-faint"></i>
              Save to Table Column
            </p>
            <div>
              <label class="block text-eyebrow font-semibold uppercase text-ink-muted mb-1.5">Store Value As</label>
              <input type="text" name="data_source[filter_config][loops][${timestamp}][storage_col]" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono" placeholder="loop_value">
            </div>
            <p class="mt-1.5 text-meta text-ink-faint">Column name to store the loop value in each data row. Leave blank to skip.</p>
          </div>
        </div>
      </div>
    `
    this.loopsContainerTarget.insertAdjacentHTML('beforeend', template)

    // Try to re-init icons if available globally
    if (window.lucide && window.lucide.createIcons) {
      window.lucide.createIcons();
    }
  }

  removeLoop(event) {
    event.preventDefault()
    event.currentTarget.closest('.loop-item').remove()
  }

  toggleLoopItem(event) {
    const select = event.target
    const wrapper = select.closest('.loop-item')
    const type = select.value

    // Hide all type-specific sections (both body panels and header dropdowns)
    wrapper.querySelectorAll('.date-fields, .array-fields').forEach(el => el.classList.add('hidden'))
    if (type === 'date') {
      wrapper.querySelectorAll('.date-fields').forEach(el => el.classList.remove('hidden'))
    } else if (type === 'array') {
      wrapper.querySelectorAll('.array-fields').forEach(el => el.classList.remove('hidden'))
    }
  }

  toggleDateStyle(event) {
    const select = event.target
    const wrapper = select.closest('.loop-item')
    const style = select.value

    wrapper.querySelectorAll('.end-date-fields').forEach(el => {
      if (style === 'range') {
        el.classList.remove('hidden')
      } else {
        el.classList.add('hidden')
      }
    })
  }

  addParam(event) {
    event.preventDefault()
    const timestamp = new Date().getTime()
    const template = `
      <div class="flex gap-2 items-center mb-2 group">
        <input type="text" name="data_source[filter_config][other_params][${timestamp}][key]" placeholder="Key" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono">
        <input type="text" name="data_source[filter_config][other_params][${timestamp}][value]" placeholder="Value" class="block w-full h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft font-mono">
        <select name="data_source[filter_config][other_params][${timestamp}][location]" class="h-ctl rounded-ctl border border-hair-hi bg-surface px-2.5 text-ui text-ink focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand-soft shrink-0">
          <option value="query">Query</option>
          <option value="header">Header</option>
        </select>
        <button type="button" data-action="data-source-form#removeParam" class="flex items-center gap-1 p-2 text-meta font-medium text-ink-faint hover:text-state-bad transition-colors" data-controller="icons">
          <i data-lucide="trash-2" class="w-4 h-4"></i>
        </button>
      </div>
    `
    this.paramsContainerTarget.insertAdjacentHTML('beforeend', template)

    if (window.lucide && window.lucide.createIcons) {
      window.lucide.createIcons();
    }
  }

  removeParam(event) {
    event.preventDefault()
    event.currentTarget.closest('.group').remove()
  }

  availableTableOptions() {
    try {
      const tables = JSON.parse(this.loopsContainerTarget.dataset.availableTables || '[]')
      return tables.map(t => `<option value="${t.value}">${t.label}</option>`).join('')
    } catch { return '' }
  }

  showLoading(event) {
    if (!event.currentTarget.form.checkValidity()) return
    if (this.hasTestResultsTarget) {
      this.testResultsTarget.innerHTML = `
            <div class="h-full flex flex-col items-center justify-center gap-2 py-10 text-center" data-controller="icons">
                <i data-lucide="loader-2" class="w-8 h-8 animate-spin text-brand"></i>
                <p class="text-ui font-semibold text-ink">Testing Connection...</p>
                <p class="text-meta text-ink-muted">This might take a moment.</p>
            </div>
          `
      if (window.lucide && window.lucide.createIcons) {
        window.lucide.createIcons();
      }
    }
  }
}
